import React from 'react';
import { motion } from 'framer-motion';
import { Card } from './ui/card';
import { Sparkles, Handshake, TrendingUp, Users } from 'lucide-react';
import { Button } from './ui/button';

const SponsorsSection = () => {
  const benefits = [
    {
      icon: Users,
      title: 'Engaged Audience',
      description: 'Reach global nomads, immigrants, and dream-chasers actively building lives abroad.'
    },
    {
      icon: TrendingUp,
      title: 'Growing Community',
      description: 'Join us as we expand our listener base across continents and cultures.'
    },
    {
      icon: Handshake,
      title: 'Authentic Partnership',
      description: 'Align your brand with genuine stories that inspire courage and transformation.'
    }
  ];

  return (
    <section id="sponsors" className="py-14 sm:py-18 lg:py-24 section-sage-mist">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="text-center"
        >
          <div className="inline-flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-primary" />
            <span className="text-sm font-mono text-muted-foreground">Partnership Opportunities</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            Become a Sponsor
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-[60ch] mx-auto leading-relaxed mb-12">
            Partner with A Life Elsewhere and support authentic stories of global reinvention that inspire thousands of listeners worldwide.
          </p>

          {/* Benefits Grid */}
          <div className="grid sm:grid-cols-3 gap-6 mb-12">
            {benefits.map((benefit) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              >
                <Card className="p-6 h-full">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 mx-auto">
                    <benefit.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-bold mb-2 text-base">{benefit.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {benefit.description}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* CTA Card */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <Card className="p-8 sm:p-10 bg-gradient-to-br from-card via-card to-primary/5">
              <Handshake className="w-12 h-12 text-primary mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-3">
                Let's Build Something Together
              </h3>
              <p className="text-muted-foreground mb-6 max-w-[50ch] mx-auto leading-relaxed">
                We're looking for partners who share our mission of celebrating courage, transformation, and the stories that connect us all.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg"
                  className="rounded-xl"
                  onClick={() => window.location.href = 'mailto:hello@april-jackson.com?subject=Sponsorship Inquiry'}
                >
                  Contact Us About Sponsorship
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="rounded-xl"
                  onClick={() => window.location.href = 'mailto:hello@april-jackson.com?subject=Partnership Information Request'}
                >
                  Request Information
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-6">
                Email us at <a href="mailto:hello@april-jackson.com" className="text-primary hover:underline">hello@april-jackson.com</a>
              </p>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default SponsorsSection;
